import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Users, Plus, X, Loader2, ChevronDown, XCircle, Trash2 } from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchNatureOfGroups, fetchMasterGroups, fetchSubGroups, fetchApprovalStatusClient, fetchApprovalStatusSubClient,
    fetchStates, fetchClientGrid, fetchClientByCode, fetchClientTaxes, submitNewClient, submitUpdateClient,
    fetchSubClientEligibility, fetchSubClientsByClientCode, fetchSubClientDetails, fetchSubClientLedger,
    submitNewSubClient, submitUpdateSubClient,
    clearMasterGroups, clearSubGroups, clearClientByCode, clearSaveResult, clearUpdateResult,
    clearSubClients, clearSubClientDetails, clearSaveSubClientResult, clearUpdateSubClientResult,
    invalidateClientEligibility, resetClient,
    selectClient,
} from '../../slices/accountsSlice/clientSlice';

// ── Shared primitives (matches AddNewITCode / BankAccountCreation conventions) ─
const inputCls = 'w-full px-3 py-2 border-2 rounded-xl text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60';
const selectCls = inputCls + ' appearance-none pr-9 cursor-pointer';
const readonlyCls = 'w-full px-3 py-2 border-2 rounded-xl text-sm bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectWrap = ({ children, loading }) => (
    <div className="relative">
        {children}
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            {loading ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
    </div>
);

const Btn = ({ onClick, disabled, loading, variant = 'primary', size = 'md', children }) => {
    const base = 'inline-flex items-center gap-2 font-semibold rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed';
    const sizes = { sm: 'px-3 py-1.5 text-xs', md: 'px-4 py-2 text-sm' };
    const variants = {
        primary: 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm',
        secondary: 'bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-indigo-400',
    };
    return (
        <button type="button" onClick={onClick} disabled={disabled || loading} className={`${base} ${sizes[size]} ${variants[variant]}`}>
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {children}
        </button>
    );
};

const Badge = ({ children, color = 'gray' }) => {
    const colors = {
        green: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-700',
        red: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-700',
        amber: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-700',
        gray: 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
    };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${colors[color] || colors.gray}`}>{children}</span>;
};

const Card = ({ children, className = '' }) => (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm ${className}`}>{children}</div>
);

const EmptyState = ({ message }) => <div className="py-10 text-center text-gray-400 dark:text-gray-500 text-sm">{message}</div>;

const Modal = ({ title, onClose, children, wide }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-t-2xl sticky top-0 z-10">
                <h2 className="text-sm font-bold text-white">{title}</h2>
                <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4 text-white" /></button>
            </div>
            <div className="p-5">{children}</div>
        </div>
    </div>
);

const ReadRow = ({ label, value }) => (
    <div>
        <Label>{label}</Label>
        <input className={readonlyCls} value={value ?? ''} readOnly />
    </div>
);

const CATEGORY_LIST = [
    { Category: 'ServiceCustomer', CategoryName: 'Service Customer' },
    { Category: 'TradingCustomer', CategoryName: 'Trading Customer' },
];

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDateForAPI = (val) => {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const statusBadge = (status) => {
    if (status === 'Approved') return <Badge color="green">Approved</Badge>;
    if (status === 'Rejected') return <Badge color="red">Rejected</Badge>;
    return <Badge color="amber">Pending</Badge>;
};

// Legacy's own literal option values for these two synthetic (non-workflow-driven) actions.
const VIEW_CLIENT_DETAILS = 'ViewClientDetails';
const ADD_NEW_SUBCLIENT = 'AddNewSubClient';
const UPDATE_SUBCLIENT = 'UpdateSubClient';

// ═════════════════════════════════════════════════════════════════════════════
// GST ROWS EDITOR (shared by Add/Update Sub Client)
// ═════════════════════════════════════════════════════════════════════════════

const GstRowsEditor = ({ rows, setRows, states, statesLoading, disabled }) => {
    const addRow = () => setRows((r) => [...r, { state: '', gstNo: '' }]);
    const removeRow = (idx) => setRows((r) => r.filter((_, i) => i !== idx));
    const updateRow = (idx, patch) => setRows((r) => r.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

    return (
        <div className="border border-gray-100 dark:border-gray-700 rounded-xl overflow-hidden">
            <table className="w-full text-xs">
                <thead>
                    <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                        <th className="px-3 py-2 font-bold text-gray-500 uppercase">State</th>
                        <th className="px-3 py-2 font-bold text-gray-500 uppercase">GST Number</th>
                        <th className="px-3 py-2 w-10"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                    {rows.map((row, idx) => (
                        <tr key={idx}>
                            <td className="px-3 py-1.5">
                                <SelectWrap loading={statesLoading}>
                                    <select className={selectCls + ' py-1'} value={row.state} onChange={(e) => updateRow(idx, { state: e.target.value })} disabled={disabled}>
                                        <option value="">-Please Select-</option>
                                        {states.map((s) => <option key={s.State_ID} value={s.State_ID}>{s.State_Name}</option>)}
                                    </select>
                                </SelectWrap>
                            </td>
                            <td className="px-3 py-1.5">
                                <input className={inputCls + ' py-1'} value={row.gstNo} onChange={(e) => updateRow(idx, { gstNo: e.target.value })} disabled={disabled} />
                            </td>
                            <td className="px-3 py-1.5 text-center">
                                <button type="button" onClick={() => removeRow(idx)} disabled={disabled} className="p-1 rounded text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30">
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="p-2 bg-gray-50 dark:bg-gray-900/40">
                <Btn variant="secondary" size="sm" onClick={addRow} disabled={disabled}>Add New Tax</Btn>
            </div>
        </div>
    );
};

const validateGstRows = (rows) => {
    if (rows.length === 0) return 'Add at least one GST row.';
    for (const r of rows) {
        if (!r.state) return 'Select State for every GST row.';
        if (!r.gstNo.trim()) return 'Enter GST Number for every GST row.';
    }
    const states = rows.map((r) => r.state);
    if (new Set(states).size !== states.length) return 'Only one GST per State.';
    return null;
};

// ═════════════════════════════════════════════════════════════════════════════
// ADD CLIENT MODAL
// ═════════════════════════════════════════════════════════════════════════════

const AddClientModal = ({ onClose, currentUser, roleId, natureGroups, natureGroupsLoading }) => {
    const dispatch = useDispatch();
    const { masterGroups, masterGroupsLoading, subGroups, subGroupsLoading, saveLoading, saveResult, saveError } = useSelector(selectClient);

    const [category, setCategory] = useState('');
    const [clientName, setClientName] = useState('');
    const [tinNo, setTinNo] = useState('');
    const [panNo, setPanNo] = useState('');
    const [tanNo, setTanNo] = useState('');
    const [contactPersonName, setContactPersonName] = useState('');
    const [phoneNo, setPhoneNo] = useState('');
    const [address, setAddress] = useState('');
    const [natureGroupId, setNatureGroupId] = useState('');
    const [groupId, setGroupId] = useState('');
    const [subGroupId, setSubGroupId] = useState('');

    useEffect(() => {
        if (saveResult) { toast.success('Client added successfully.'); dispatch(clearSaveResult()); onClose(); }
        if (saveError) { toast.error(saveError); dispatch(clearSaveResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResult, saveError]);

    const handleNatureChange = (val) => {
        setNatureGroupId(val); setGroupId(''); setSubGroupId('');
        dispatch(clearMasterGroups());
        if (val) dispatch(fetchMasterGroups(val));
    };

    const handleGroupChange = (val) => {
        setGroupId(val); setSubGroupId('');
        dispatch(clearSubGroups());
        if (val) dispatch(fetchSubGroups(val));
    };

    const handleSubmit = () => {
        if (!category) { toast.warning('Select Client Classification.'); return; }
        if (!clientName.trim()) { toast.warning('Enter Client Name.'); return; }
        if (!tinNo.trim()) { toast.warning('Enter TIN No.'); return; }
        if (!panNo.trim()) { toast.warning('Enter PAN No.'); return; }
        if (!tanNo.trim()) { toast.warning('Enter TAN No.'); return; }
        if (!contactPersonName.trim()) { toast.warning('Enter Contact Person Name.'); return; }
        if (!phoneNo.trim()) { toast.warning('Enter Contact Person Phone No.'); return; }
        if (phoneNo.length !== 10) { toast.warning('Enter a 10 digit Phone No.'); return; }
        if (!address.trim()) { toast.warning('Enter Address.'); return; }
        if (!natureGroupId) { toast.warning('Select Nature Of Group.'); return; }
        if (!groupId) { toast.warning('Select Group.'); return; }
        if (subGroups.length > 0 && !subGroupId) { toast.warning('Select Sub Group.'); return; }

        dispatch(submitNewClient({
            Category: category,
            Client_Name: clientName.trim(),
            TINNo: tinNo.trim(),
            PANNo: panNo.trim(),
            TANNo: tanNo.trim(),
            Contact_Person_Name: contactPersonName.trim(),
            Person_PhoneNo: phoneNo,
            Address: address.trim(),
            Action: 1,
            NatureGroupId: natureGroupId,
            Createdby: currentUser,
            GroupId: groupId,
            SubGroupId: subGroupId ? parseInt(subGroupId, 10) : 0,
            RoleId: parseInt(roleId, 10) || 0,
        }));
    };

    return (
        <Modal title="Client Creation" onClose={onClose}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label required>Client Classification</Label>
                    <SelectWrap>
                        <select className={selectCls} value={category} onChange={(e) => setCategory(e.target.value)} disabled={saveLoading}>
                            <option value="">-Please Select-</option>
                            {CATEGORY_LIST.map((c) => <option key={c.Category} value={c.Category}>{c.CategoryName}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                <div>
                    <Label required>Client Name</Label>
                    <input className={inputCls} value={clientName} onChange={(e) => setClientName(e.target.value)} disabled={saveLoading} />
                </div>
                <div>
                    <Label required>TIN No</Label>
                    <input className={inputCls} value={tinNo} onChange={(e) => setTinNo(e.target.value)} disabled={saveLoading} />
                </div>
                <div>
                    <Label required>PAN No</Label>
                    <input className={inputCls} value={panNo} onChange={(e) => setPanNo(e.target.value)} disabled={saveLoading} />
                </div>
                <div>
                    <Label required>TAN No</Label>
                    <input className={inputCls} value={tanNo} onChange={(e) => setTanNo(e.target.value)} disabled={saveLoading} />
                </div>
                <div>
                    <Label required>Contact Person Name</Label>
                    <input className={inputCls} value={contactPersonName} onChange={(e) => setContactPersonName(e.target.value)} disabled={saveLoading} />
                </div>
                <div>
                    <Label required>Nature Of Group</Label>
                    <SelectWrap loading={natureGroupsLoading}>
                        <select className={selectCls} value={natureGroupId} onChange={(e) => handleNatureChange(e.target.value)} disabled={saveLoading}>
                            <option value="">-Please Select-</option>
                            {natureGroups.map((n) => <option key={n.NatureGroupId} value={n.NatureGroupId}>{n.NatureGroupName}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                <div>
                    <Label required>Contact Person Phone No</Label>
                    <input className={inputCls} value={phoneNo} onChange={(e) => setPhoneNo(e.target.value.replace(/\D/g, '').slice(0, 10))} disabled={saveLoading} inputMode="numeric" maxLength={10} />
                </div>
                <div>
                    <Label required>Master Group</Label>
                    <SelectWrap loading={masterGroupsLoading}>
                        <select className={selectCls} value={groupId} onChange={(e) => handleGroupChange(e.target.value)} disabled={saveLoading || !natureGroupId}>
                            <option value="">-Please Select-</option>
                            {masterGroups.map((g) => <option key={g.GroupId} value={g.GroupId}>{g.GroupName}</option>)}
                        </select>
                    </SelectWrap>
                </div>
                {subGroups.length > 0 && (
                    <div>
                        <Label required>Sub Group</Label>
                        <SelectWrap loading={subGroupsLoading}>
                            <select className={selectCls} value={subGroupId} onChange={(e) => setSubGroupId(e.target.value)} disabled={saveLoading}>
                                <option value="">-Please Select-</option>
                                {subGroups.map((s) => <option key={s.Id} value={s.Id}>{s.Name}</option>)}
                            </select>
                        </SelectWrap>
                    </div>
                )}
                <div className="md:col-span-2">
                    <Label required>Address</Label>
                    <textarea rows={3} className={inputCls + ' resize-none'} value={address} onChange={(e) => setAddress(e.target.value)} disabled={saveLoading} />
                </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={saveLoading}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={saveLoading}>Submit</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// EDIT CLIENT MODAL
// ═════════════════════════════════════════════════════════════════════════════

const EditClientModal = ({ row, onClose, currentUser, roleId }) => {
    const dispatch = useDispatch();
    const { clientByCode, clientByCodeLoading, updateLoading, updateResult, updateError } = useSelector(selectClient);

    const [tinNo, setTinNo] = useState('');
    const [panNo, setPanNo] = useState('');
    const [tanNo, setTanNo] = useState('');
    const [contactPersonName, setContactPersonName] = useState('');
    const [phoneNo, setPhoneNo] = useState('');
    const [address, setAddress] = useState('');

    useEffect(() => {
        dispatch(fetchClientByCode(row.Client_Code));
        return () => { dispatch(clearClientByCode()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, row.Client_Code]);

    useEffect(() => {
        if (clientByCode) {
            setTinNo(clientByCode.TINNo || '');
            setPanNo(clientByCode.PANNo || '');
            setTanNo(clientByCode.TANNo || '');
            setContactPersonName(clientByCode.Contact_Person_Name || '');
            setPhoneNo(clientByCode.Person_PhoneNo || '');
            setAddress(clientByCode.Address || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientByCode]);

    useEffect(() => {
        if (updateResult) { toast.success('Client updated successfully.'); dispatch(clearUpdateResult()); onClose(); }
        if (updateError) { toast.error(updateError); dispatch(clearUpdateResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateResult, updateError]);

    const busy = updateLoading || clientByCodeLoading;

    const handleSubmit = () => {
        if (!tinNo.trim()) { toast.warning('Enter TIN No.'); return; }
        if (!panNo.trim()) { toast.warning('Enter PAN No.'); return; }
        if (!tanNo.trim()) { toast.warning('Enter TAN No.'); return; }
        if (!contactPersonName.trim()) { toast.warning('Enter Contact Person Name.'); return; }
        if (!phoneNo.trim()) { toast.warning('Enter Contact Person Phone No.'); return; }
        if (phoneNo.length !== 10) { toast.warning('Enter a 10 digit Phone No.'); return; }
        if (!address.trim()) { toast.warning('Enter Address.'); return; }

        dispatch(submitUpdateClient({
            Client_Code: row.Client_Code,
            Client_Name: row.Client_Name,
            TINNo: tinNo.trim(),
            PANNo: panNo.trim(),
            TANNo: tanNo.trim(),
            Contact_Person_Name: contactPersonName.trim(),
            Person_PhoneNo: phoneNo,
            Address: address.trim(),
            Action: 2,
            Createdby: currentUser,
            RoleId: parseInt(roleId, 10) || 0,
            CheckUpdationType: row.ClientStatus === 'Approved' ? 'Insert' : 'ReturnUpdate',
        }));
    };

    if (clientByCodeLoading && !clientByCode) {
        return <Modal title="Update Client" onClose={onClose}><div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-500" /></div></Modal>;
    }

    return (
        <Modal title={`Update Client — ${row.Client_Code}`} onClose={onClose}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ReadRow label="Client Code" value={row.Client_Code} />
                <ReadRow label="Client Name" value={row.Client_Name} />
                <div>
                    <Label required>TIN No</Label>
                    <input className={inputCls} value={tinNo} onChange={(e) => setTinNo(e.target.value)} disabled={busy} />
                </div>
                <div>
                    <Label required>PAN No</Label>
                    <input className={inputCls} value={panNo} onChange={(e) => setPanNo(e.target.value)} disabled={busy} />
                </div>
                <div>
                    <Label required>TAN No</Label>
                    <input className={inputCls} value={tanNo} onChange={(e) => setTanNo(e.target.value)} disabled={busy} />
                </div>
                <div>
                    <Label required>Contact Person Name</Label>
                    <input className={inputCls} value={contactPersonName} onChange={(e) => setContactPersonName(e.target.value)} disabled={busy} />
                </div>
                <div>
                    <Label required>Contact Person Phone No</Label>
                    <input className={inputCls} value={phoneNo} onChange={(e) => setPhoneNo(e.target.value.replace(/\D/g, '').slice(0, 10))} disabled={busy} inputMode="numeric" maxLength={10} />
                </div>
                <ReadRow label="Nature Of Group" value={row.NatureGroupName} />
                <ReadRow label="Group Name" value={row.GroupName} />
                {row.SubGroupId ? <ReadRow label="Sub Group Name" value={row.SubGroupName} /> : null}
                <div className="md:col-span-2">
                    <Label required>Address</Label>
                    <textarea rows={3} className={inputCls + ' resize-none'} value={address} onChange={(e) => setAddress(e.target.value)} disabled={busy} />
                </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={busy}>Submit</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// VIEW CLIENT DETAILS MODAL (read-only)
// ═════════════════════════════════════════════════════════════════════════════

const ViewClientDetailsModal = ({ row, onClose }) => {
    const dispatch = useDispatch();
    const { clientByCode, clientByCodeLoading, clientTaxes, clientTaxesLoading } = useSelector(selectClient);

    useEffect(() => {
        dispatch(fetchClientByCode(row.Client_Code));
        return () => { dispatch(clearClientByCode()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, row.Client_Code]);

    useEffect(() => {
        if (clientByCode?.GST_Applicable === 'True') dispatch(fetchClientTaxes(row.Client_Code));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientByCode]);

    if (clientByCodeLoading || !clientByCode) {
        return <Modal title="Client Details" onClose={onClose}><div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-500" /></div></Modal>;
    }

    const hasLedger = parseInt(clientByCode.LedgerId, 10) > 0;

    return (
        <Modal title="Client Details" onClose={onClose}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ReadRow label="Client Name" value={clientByCode.Client_Name} />
                <ReadRow label="TIN Number" value={clientByCode.TINNo} />
                <ReadRow label="PAN Number" value={clientByCode.PANNo} />
                <ReadRow label="TAN Number" value={clientByCode.TANNo} />
                <ReadRow label="Contact Person Name" value={clientByCode.Contact_Person_Name} />
                <ReadRow label="Client Phone Number" value={clientByCode.Person_PhoneNo} />
                <ReadRow label="GST Applicable" value={clientByCode.GST_Applicable === 'True' ? 'Yes' : 'No'} />
                <ReadRow label="Nature Of Group" value={clientByCode.NatureGroupName} />
                <ReadRow label="Group Name" value={clientByCode.GroupName} />
                {clientByCode.SubGroupId ? <ReadRow label="Sub Group Name" value={clientByCode.SubGroupName} /> : null}
                {hasLedger && (
                    <>
                        <ReadRow label="Opening Balance" value={clientByCode.OpeningBalance} />
                        <ReadRow label="Balance As On" value={clientByCode.BalanceAsOnDate} />
                        <ReadRow label="Ledger Value Type" value={clientByCode.LedgerValueType} />
                    </>
                )}
                <ReadRow label="Address" value={clientByCode.Address} />
            </div>

            {clientByCode.GST_Applicable === 'True' && (
                <div className="mt-4">
                    <Label>GST Details</Label>
                    {clientTaxesLoading ? (
                        <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-indigo-500" /></div>
                    ) : clientTaxes.length === 0 ? (
                        <p className="text-xs text-gray-400">No GST details found.</p>
                    ) : (
                        <div className="border border-gray-100 dark:border-gray-700 rounded-xl overflow-hidden">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                        <th className="px-3 py-2 font-bold text-gray-500 uppercase">State</th>
                                        <th className="px-3 py-2 font-bold text-gray-500 uppercase">GST No</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                    {clientTaxes.map((t, i) => (
                                        <tr key={i}>
                                            <td className="px-3 py-1.5">{t.State}</td>
                                            <td className="px-3 py-1.5">{t.TaxNo}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose}>Close</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// ADD NEW SUB CLIENT MODAL
// ═════════════════════════════════════════════════════════════════════════════

const AddSubClientModal = ({ row, onClose, currentUser, roleId, states, statesLoading }) => {
    const dispatch = useDispatch();
    const { saveSubClientLoading, saveSubClientResult, saveSubClientError } = useSelector(selectClient);

    const [branch, setBranch] = useState('');
    const [personName, setPersonName] = useState('');
    const [phoneNo, setPhoneNo] = useState('');
    const [tinNo, setTinNo] = useState('');
    const [panNo, setPanNo] = useState('');
    const [tanNo, setTanNo] = useState('');
    const [address, setAddress] = useState('');
    const [openingBalance, setOpeningBalance] = useState('');
    const [balanceAsOnDate, setBalanceAsOnDate] = useState(new Date());
    const [ledgerValueType, setLedgerValueType] = useState('');
    const [gstApplicable, setGstApplicable] = useState(false);
    const [gstRows, setGstRows] = useState([]);

    useEffect(() => {
        if (saveSubClientResult) {
            toast.success('Sub Client added successfully.');
            dispatch(clearSaveSubClientResult());
            dispatch(invalidateClientEligibility(row.Client_Code));
            onClose();
        }
        if (saveSubClientError) { toast.error(saveSubClientError); dispatch(clearSaveSubClientResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveSubClientResult, saveSubClientError]);

    const handleSubmit = () => {
        if (!branch.trim()) { toast.warning('Enter Branch.'); return; }
        if (!personName.trim()) { toast.warning('Enter Person Name.'); return; }
        if (!phoneNo.trim()) { toast.warning('Enter Person Phone No.'); return; }
        if (phoneNo.length !== 10) { toast.warning('Enter a 10 digit Phone No.'); return; }
        if (!tinNo.trim()) { toast.warning('Enter TIN No.'); return; }
        if (!panNo.trim()) { toast.warning('Enter PAN No.'); return; }
        if (!tanNo.trim()) { toast.warning('Enter TAN No.'); return; }
        if (!address.trim()) { toast.warning('Enter Address.'); return; }
        if (!openingBalance || isNaN(parseFloat(openingBalance))) { toast.warning('Enter Opening Balance.'); return; }
        if (!(balanceAsOnDate instanceof Date)) { toast.warning('Select Balance As On date.'); return; }
        if (!ledgerValueType) { toast.warning('Select Ledger Value Type.'); return; }
        if (gstApplicable) {
            const gstErr = validateGstRows(gstRows);
            if (gstErr) { toast.warning(gstErr); return; }
        }

        dispatch(submitNewSubClient({
            ClientCode: row.Client_Code,
            Branch: branch.trim(),
            SC_Person_Name: personName.trim(),
            SC_Phone_No: phoneNo,
            SC_TINNo: tinNo.trim(),
            SC_PANNo: panNo.trim(),
            SC_TANNo: tanNo.trim(),
            Address: address.trim(),
            Action: 1,
            NatureGroupId: row.Nid,
            Createdby: currentUser,
            RoleId: parseInt(roleId, 10) || 0,
            GST_Applicable: gstApplicable ? '1' : '0',
            GSTStatesList: gstApplicable ? gstRows.map((r) => r.state).join(',') + ',' : '',
            GSTNoList: gstApplicable ? gstRows.map((r) => r.gstNo).join(',') + ',' : '',
            LedgerValueType: ledgerValueType,
            OpeningBalance: parseFloat(openingBalance),
            BalanceAsOnDate: formatDateForAPI(balanceAsOnDate),
        }));
    };

    return (
        <Modal title={`Add New Sub Client — ${row.Client_Code}`} onClose={onClose} wide>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ReadRow label="Client Code" value={row.Client_Code} />
                <ReadRow label="Nature Of Group" value={row.NatureGroupName} />
                <div>
                    <Label required>Branch</Label>
                    <input className={inputCls} value={branch} onChange={(e) => setBranch(e.target.value)} disabled={saveSubClientLoading} />
                </div>
                <div>
                    <Label required>Contact Person Name</Label>
                    <input className={inputCls} value={personName} onChange={(e) => setPersonName(e.target.value)} disabled={saveSubClientLoading} />
                </div>
                <div>
                    <Label required>Contact Person Phone No</Label>
                    <input className={inputCls} value={phoneNo} onChange={(e) => setPhoneNo(e.target.value.replace(/\D/g, '').slice(0, 10))} disabled={saveSubClientLoading} inputMode="numeric" maxLength={10} />
                </div>
                <div>
                    <Label required>TIN No</Label>
                    <input className={inputCls} value={tinNo} onChange={(e) => setTinNo(e.target.value)} disabled={saveSubClientLoading} />
                </div>
                <div>
                    <Label required>PAN No</Label>
                    <input className={inputCls} value={panNo} onChange={(e) => setPanNo(e.target.value)} disabled={saveSubClientLoading} />
                </div>
                <div>
                    <Label required>TAN No</Label>
                    <input className={inputCls} value={tanNo} onChange={(e) => setTanNo(e.target.value)} disabled={saveSubClientLoading} />
                </div>
                <div>
                    <Label required>Opening Balance</Label>
                    <input className={inputCls} value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} disabled={saveSubClientLoading} inputMode="decimal" />
                </div>
                <div>
                    <Label required>Balance As On</Label>
                    <CustomDatePicker value={balanceAsOnDate} onChange={setBalanceAsOnDate} format="DD-MMM-YYYY" placeholder="Select date" maxDate={new Date()} />
                </div>
                <div className="md:col-span-2">
                    <Label required>Address</Label>
                    <textarea rows={2} className={inputCls + ' resize-none'} value={address} onChange={(e) => setAddress(e.target.value)} disabled={saveSubClientLoading} />
                </div>
                <div>
                    <Label required>Ledger Value Type</Label>
                    <div className="flex gap-4 mt-1">
                        {['Credit', 'Debit'].map((t) => (
                            <label key={t} className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                                <input type="radio" name="scLedgerValueType" checked={ledgerValueType === t} onChange={() => setLedgerValueType(t)} disabled={saveSubClientLoading} />
                                {t}
                            </label>
                        ))}
                    </div>
                </div>
                <div>
                    <Label required>GST Applicable</Label>
                    <div className="flex gap-4 mt-1">
                        {[{ v: true, t: 'Yes' }, { v: false, t: 'No' }].map((o) => (
                            <label key={o.t} className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                                <input type="radio" name="scGstApplicable" checked={gstApplicable === o.v} onChange={() => { setGstApplicable(o.v); if (o.v && gstRows.length === 0) setGstRows([{ state: '', gstNo: '' }]); }} disabled={saveSubClientLoading} />
                                {o.t}
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            {gstApplicable && (
                <div className="mt-4">
                    <Label required>GST Details</Label>
                    <GstRowsEditor rows={gstRows} setRows={setGstRows} states={states} statesLoading={statesLoading} disabled={saveSubClientLoading} />
                </div>
            )}

            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={saveSubClientLoading}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={saveSubClientLoading}>Submit</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// UPDATE SUB CLIENT MODAL
// ═════════════════════════════════════════════════════════════════════════════

const UpdateSubClientModal = ({ row, onClose, currentUser, roleId, states, statesLoading }) => {
    const dispatch = useDispatch();
    const {
        subClients, subClientsLoading, subClientDetails, subClientDetailsLoading, subClientLedger,
        clientTaxes, clientTaxesLoading,
        updateSubClientLoading, updateSubClientResult, updateSubClientError,
    } = useSelector(selectClient);

    const [subClientCode, setSubClientCode] = useState('');
    const [branch, setBranch] = useState('');
    const [personName, setPersonName] = useState('');
    const [phoneNo, setPhoneNo] = useState('');
    const [tinNo, setTinNo] = useState('');
    const [panNo, setPanNo] = useState('');
    const [tanNo, setTanNo] = useState('');
    const [address, setAddress] = useState('');
    const [gstApplicable, setGstApplicable] = useState(false);
    const [gstRows, setGstRows] = useState([]);

    useEffect(() => {
        dispatch(fetchSubClientsByClientCode(row.Client_Code));
        return () => { dispatch(clearSubClients()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, row.Client_Code]);

    useEffect(() => {
        if (subClientDetails) {
            setBranch(subClientDetails.Branch || '');
            setPersonName(subClientDetails.SC_Person_Name || '');
            setPhoneNo(subClientDetails.SC_Phone_No || '');
            setTinNo(subClientDetails.SC_TINNo || '');
            setPanNo(subClientDetails.SC_PANNo || '');
            setTanNo(subClientDetails.SC_TANNo || '');
            setAddress(subClientDetails.Address || '');
            const applicable = subClientDetails.GST_Applicable === 'YES';
            setGstApplicable(applicable);
            if (applicable) dispatch(fetchClientTaxes(subClientCode));
            else setGstRows([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subClientDetails]);

    useEffect(() => {
        if (subClientDetails?.GST_Applicable === 'YES' && clientTaxes) {
            setGstRows(clientTaxes.map((t) => ({ state: String(t.State_Id), gstNo: t.TaxNo })));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientTaxes]);

    useEffect(() => {
        if (updateSubClientResult) { toast.success('Sub Client updated successfully.'); dispatch(clearUpdateSubClientResult()); onClose(); }
        if (updateSubClientError) { toast.error(updateSubClientError); dispatch(clearUpdateSubClientResult()); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateSubClientResult, updateSubClientError]);

    const handleSelectSubClient = (code) => {
        setSubClientCode(code);
        dispatch(clearSubClientDetails());
        setGstRows([]);
        if (code) {
            dispatch(fetchSubClientDetails({ clientCode: row.Client_Code, subClient: code }));
            dispatch(fetchSubClientLedger(code));
        }
    };

    const busy = updateSubClientLoading || subClientDetailsLoading;

    const handleSubmit = () => {
        if (!subClientCode) { toast.warning('Select a Sub Client.'); return; }
        if (!branch.trim()) { toast.warning('Enter Branch.'); return; }
        if (!personName.trim()) { toast.warning('Enter Person Name.'); return; }
        if (!phoneNo.trim()) { toast.warning('Enter Person Phone No.'); return; }
        if (phoneNo.length !== 10) { toast.warning('Enter a 10 digit Phone No.'); return; }
        if (!tinNo.trim()) { toast.warning('Enter TIN No.'); return; }
        if (!panNo.trim()) { toast.warning('Enter PAN No.'); return; }
        if (!tanNo.trim()) { toast.warning('Enter TAN No.'); return; }
        if (!address.trim()) { toast.warning('Enter Address.'); return; }
        if (gstApplicable) {
            const gstErr = validateGstRows(gstRows);
            if (gstErr) { toast.warning(gstErr); return; }
        }

        dispatch(submitUpdateSubClient({
            ClientCode: row.Client_Code,
            SubClientCode: subClientCode,
            Branch: branch.trim(),
            SC_Person_Name: personName.trim(),
            SC_Phone_No: phoneNo,
            SC_TINNo: tinNo.trim(),
            SC_PANNo: panNo.trim(),
            SC_TANNo: tanNo.trim(),
            Address: address.trim(),
            Action: 2,
            Createdby: currentUser,
            RoleId: parseInt(roleId, 10) || 0,
            CheckUpdationType: 'Insert',
            GST_Applicable: gstApplicable ? '1' : '0',
            GSTStatesList: gstApplicable ? gstRows.map((r) => r.state).join(',') + ',' : '',
            GSTNoList: gstApplicable ? gstRows.map((r) => r.gstNo).join(',') + ',' : '',
            // Preserve the sub client's real ledger values — spInsertUpdateSubClient's Update
            // branch unconditionally overwrites the ledger row with whatever it's given, and
            // this page doesn't expose ledger editing (see fetchSubClientLedger for why).
            LedgerValueType: subClientLedger?.ValueType || '',
            OpeningBalance: subClientLedger?.Balance ?? 0,
            BalanceAsOnDate: subClientLedger?.Date || formatDateForAPI(new Date()),
        }));
    };

    return (
        <Modal title={`Update Sub Client — ${row.Client_Code}`} onClose={onClose} wide>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <ReadRow label="Client Code" value={row.Client_Code} />
                <div>
                    <Label required>Sub Client Code</Label>
                    <SelectWrap loading={subClientsLoading}>
                        <select className={selectCls} value={subClientCode} onChange={(e) => handleSelectSubClient(e.target.value)} disabled={busy}>
                            <option value="">-Please Select-</option>
                            {subClients.map((s) => <option key={s.SubClientId} value={s.SubClientCode}>{s.SubClientCode}</option>)}
                        </select>
                    </SelectWrap>
                    {!subClientsLoading && subClients.length === 0 && (
                        <p className="text-xs text-rose-500 mt-1">Sub Client verification is pending — none available to update yet.</p>
                    )}
                </div>
            </div>

            {subClientCode && (subClientDetailsLoading ? (
                <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-500" /></div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label required>Branch</Label>
                            <input className={inputCls} value={branch} onChange={(e) => setBranch(e.target.value)} disabled={busy} />
                        </div>
                        <div>
                            <Label required>Contact Person Name</Label>
                            <input className={inputCls} value={personName} onChange={(e) => setPersonName(e.target.value)} disabled={busy} />
                        </div>
                        <div>
                            <Label required>Contact Person Phone No</Label>
                            <input className={inputCls} value={phoneNo} onChange={(e) => setPhoneNo(e.target.value.replace(/\D/g, '').slice(0, 10))} disabled={busy} inputMode="numeric" maxLength={10} />
                        </div>
                        <div>
                            <Label required>TIN No</Label>
                            <input className={inputCls} value={tinNo} onChange={(e) => setTinNo(e.target.value)} disabled={busy} />
                        </div>
                        <div>
                            <Label required>PAN No</Label>
                            <input className={inputCls} value={panNo} onChange={(e) => setPanNo(e.target.value)} disabled={busy} />
                        </div>
                        <div>
                            <Label required>TAN No</Label>
                            <input className={inputCls} value={tanNo} onChange={(e) => setTanNo(e.target.value)} disabled={busy} />
                        </div>
                        <ReadRow label="Nature Of Group" value={subClientDetails?.NatureGroupName} />
                        <div>
                            <Label required>GST Applicable</Label>
                            <div className="flex gap-4 mt-1">
                                {[{ v: true, t: 'Yes' }, { v: false, t: 'No' }].map((o) => (
                                    <label key={o.t} className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                                        <input type="radio" name="upScGstApplicable" checked={gstApplicable === o.v} onChange={() => { setGstApplicable(o.v); if (o.v && gstRows.length === 0) setGstRows([{ state: '', gstNo: '' }]); }} disabled={busy} />
                                        {o.t}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <Label required>Address</Label>
                            <textarea rows={2} className={inputCls + ' resize-none'} value={address} onChange={(e) => setAddress(e.target.value)} disabled={busy} />
                        </div>
                    </div>

                    {gstApplicable && (
                        <div className="mt-4">
                            <Label required>GST Details</Label>
                            {clientTaxesLoading ? (
                                <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-indigo-500" /></div>
                            ) : (
                                <GstRowsEditor rows={gstRows} setRows={setGstRows} states={states} statesLoading={statesLoading} disabled={busy} />
                            )}
                        </div>
                    )}
                </>
            ))}

            <div className="flex justify-end gap-2 mt-5">
                <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
                <Btn onClick={handleSubmit} loading={busy} disabled={!subClientCode}>Submit</Btn>
            </div>
        </Modal>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════════

const ClientCreation = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;
    const currentUser = userData?.userName || userData?.userId || userData?.UID || userData?.uid || '';

    const {
        natureGroups, natureGroupsLoading, grid, gridLoading, gridError,
        allowedActions, allowedActionsSubClient, states, statesLoading,
        eligibilityByClient, eligibilityLoadingFor,
    } = useSelector(selectClient);

    const [showAdd, setShowAdd] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [viewRow, setViewRow] = useState(null);
    const [addSubClientRow, setAddSubClientRow] = useState(null);
    const [updateSubClientRow, setUpdateSubClientRow] = useState(null);

    useEffect(() => {
        dispatch(fetchNatureOfGroups());
        dispatch(fetchClientGrid());
        dispatch(fetchApprovalStatusClient(roleId));
        dispatch(fetchApprovalStatusSubClient(roleId));
        dispatch(fetchStates());
        return () => { dispatch(resetClient()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const canAct = (row) => row.ClientStatus === 'Approved';

    const refreshGrid = () => dispatch(fetchClientGrid());

    const handleRowFocus = (row) => {
        if (!(row.Client_Code in eligibilityByClient) && eligibilityLoadingFor !== row.Client_Code) {
            dispatch(fetchSubClientEligibility({ clientCode: row.Client_Code, roleId }));
        }
    };

    const handleActionChange = (row, action) => {
        if (!action || action === 'Select' || action === '0') return;
        if (action === 'Edit' || action === 'UpdateClient') { setEditRow(row); return; }
        if (action === VIEW_CLIENT_DETAILS) { setViewRow(row); return; }
        if (action === ADD_NEW_SUBCLIENT) { setAddSubClientRow(row); return; }
        if (action === UPDATE_SUBCLIENT) { setUpdateSubClientRow(row); return; }
        toast.info(`"${action}" isn't available on this page yet.`);
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 px-6 py-8 shadow-lg">
                <div className="max-w-7xl mx-auto flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl"><Users className="h-5 w-5 text-white" /></div>
                    <div>
                        <h1 className="text-xl font-bold text-white">{menuData?.name || 'Client And SubClients'}</h1>
                        <p className="text-indigo-200 text-xs mt-0.5">Register and manage clients and sub clients</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
                <Card>
                    <div className="p-4 flex flex-wrap items-center justify-end gap-3">
                        <Btn onClick={() => setShowAdd(true)}><Plus className="h-3.5 w-3.5" />Add Client</Btn>
                    </div>
                </Card>

                <Card>
                    {gridLoading ? (
                        <div className="py-14 flex justify-center"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
                    ) : gridError ? (
                        <div className="py-10 text-center text-sm text-rose-500">{gridError}</div>
                    ) : grid.length === 0 ? (
                        <EmptyState message="No Clients found." />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                        {['Client Code', 'Client Name', 'Contact Person', 'Address', 'Status', ''].map((h) => (
                                            <th key={h} className="px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                    {grid.map((row) => {
                                        const eligibility = eligibilityByClient[row.Client_Code];
                                        const isEligibilityLoading = eligibilityLoadingFor === row.Client_Code;
                                        const options = [
                                            ...allowedActions.map((a, i) => ({ key: `c-${i}`, value: a.Value, text: a.Text })),
                                            { key: 'view', value: VIEW_CLIENT_DETAILS, text: 'View Client Details' },
                                        ];
                                        if (eligibility?.WFExist === 1) {
                                            options.push({ key: 'addsc', value: ADD_NEW_SUBCLIENT, text: 'Add New Sub Client' });
                                        }
                                        if (eligibility?.SubClientCExist === 1) {
                                            allowedActionsSubClient.forEach((a, i) => options.push({ key: `sc-${i}`, value: a.Value, text: a.Text }));
                                        }
                                        return (
                                            <tr key={row.ClientID} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                                <td className="px-3 py-2.5 font-mono text-gray-700 dark:text-gray-300">{row.Client_Code}</td>
                                                <td className="px-3 py-2.5 text-gray-800 dark:text-gray-100">{row.Client_Name}</td>
                                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300">{row.Contact_Person_Name}</td>
                                                <td className="px-3 py-2.5 text-gray-500 text-xs">{row.Address}</td>
                                                <td className="px-3 py-2.5">{statusBadge(row.ClientStatus)}</td>
                                                <td className="px-3 py-2.5">
                                                    {canAct(row) ? (
                                                        <SelectWrap loading={isEligibilityLoading}>
                                                            <select
                                                                className={selectCls + ' py-1 text-xs'}
                                                                defaultValue=""
                                                                onFocus={() => handleRowFocus(row)}
                                                                onChange={(e) => { handleActionChange(row, e.target.value); e.target.value = ''; }}
                                                            >
                                                                <option value="">-Select-</option>
                                                                {options.map((o) => <option key={o.key} value={o.value}>{o.text}</option>)}
                                                            </select>
                                                        </SelectWrap>
                                                    ) : (
                                                        <span className="text-xs text-gray-300 dark:text-gray-600 inline-flex items-center gap-1">
                                                            <XCircle className="h-3 w-3" /> —
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>

            {showAdd && (
                <AddClientModal
                    onClose={() => { setShowAdd(false); refreshGrid(); }}
                    currentUser={currentUser} roleId={roleId}
                    natureGroups={natureGroups} natureGroupsLoading={natureGroupsLoading}
                />
            )}
            {editRow && (
                <EditClientModal
                    row={editRow} onClose={() => { setEditRow(null); refreshGrid(); }}
                    currentUser={currentUser} roleId={roleId}
                />
            )}
            {viewRow && (
                <ViewClientDetailsModal row={viewRow} onClose={() => setViewRow(null)} />
            )}
            {addSubClientRow && (
                <AddSubClientModal
                    row={addSubClientRow} onClose={() => setAddSubClientRow(null)}
                    currentUser={currentUser} roleId={roleId}
                    states={states} statesLoading={statesLoading}
                />
            )}
            {updateSubClientRow && (
                <UpdateSubClientModal
                    row={updateSubClientRow} onClose={() => setUpdateSubClientRow(null)}
                    currentUser={currentUser} roleId={roleId}
                    states={states} statesLoading={statesLoading}
                />
            )}
        </div>
    );
};

export default ClientCreation;
